import React, { Children, type ReactNode } from 'react';
import { useContainerContext as useContainerContextDI } from '@jira/platform__services-providers-container-context/src/main.tsx';
import { isProjectContext } from '@jira/platform__services-providers-container-context/src/types.tsx';
import { useServiceDeskAnalyticsContext as useServiceDeskAnalyticsContextDI } from '@jira/router-resources__service-desk-analytics-context/src/useServiceDeskAnalyticsContext.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';

type Props = {
	children: ReactNode;
	forceRenderWhileLoading?: boolean;
	useServiceDeskAnalyticsContext?: typeof useServiceDeskAnalyticsContextDI;
	useContainerContext?: typeof useContainerContextDI;
};

export const ServiceDeskAnalyticsContext = ({
	useContainerContext = useContainerContextDI,
	useServiceDeskAnalyticsContext = useServiceDeskAnalyticsContextDI,
	children,
	forceRenderWhileLoading = false,
}: Props): React.JSX.Element | null => {
	const [{ data: containerContextData, loading: containerContextLoading }] = useContainerContext();

	const {
		data: analyticsContextAttributes,
		error: analyticsContextError,
		loading: analyticsContextLoading,
	} = useServiceDeskAnalyticsContext();

	if (Children.count(children) > 0) {
		const loading = containerContextLoading || analyticsContextLoading;
		const shouldRenderChildren =
			!loading || (loading && forceRenderWhileLoading) || !!analyticsContextError;

		if (!shouldRenderChildren) {
			return null;
		}

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		const context: Record<string, any> = {};
		if (containerContextData && isProjectContext(containerContextData)) {
			const { id, type } = containerContextData.project;
			// containerType and containerId are obsolete - See https://developer.atlassian.com/platform/analytics-pipeline/getting-started/event-types/#glossary-of-terms
			// Also these dont work with screen events. See sendScreenEvent in: https://bitbucket.org/atlassian/analytics-web-client/src/master/src/analyticsWebClient.js
			// However they are kept here to not break any existing usages.
			context.containerType = 'project';
			context.containerId = `${id}`;
			// This is the preferred way to send container data for analytics
			context.containers = {
				project: {
					id: `${id}`,
					type,
				},
			};

			if (analyticsContextAttributes && !analyticsContextError) {
				context.attributes = analyticsContextAttributes;
			}
		}

		return <ContextualAnalyticsData {...context}>{children}</ContextualAnalyticsData>;
	}
	return <>{children}</>;
};
